import React, {useState, useEffect, JSX} from 'react';
import { Card, Row, Col, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useData } from '../Data/DataContext';
import CustomContainer from '../Utils/CustomContainer';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool, faClock, faMapMarkerAlt, faInfoCircle, faTachometerAlt } from '@fortawesome/free-solid-svg-icons';
import './ClientStandIn.css';
import ClientDetailModal from '../ClientDetail/ClientDetailModal';
import {IClient} from "../../utils/FrontendDataTypes";
import {employeeNeedsToChooseStandIn} from "../../utils/utils";
import {format} from "date-fns";
import {de} from "date-fns/locale";

const ClientStandIn: React.FC = () => {
  const { data, loading, error, refreshData } = useData();
  const navigate = useNavigate();

  useEffect(() => {
    if (!data || !employeeNeedsToChooseStandIn(data)) {
      navigate('/dashboard');
    }
  }, [data, navigate]);

  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [confirmSuccess, setConfirmSuccess] = useState<boolean>(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Funktion zum Anzeigen des Detailmodals
  const handleShowDetails = (client: IClient) => {
    setSelectedClient(client);
    setShowDetailModal(true);
  };

  // Funktion zum Schließen des Detailmodals
  const handleCloseModal = () => {
    setShowDetailModal(false);
    setConfirmError(null);
  };

  // Funktion zum Bestätigen der Vertretung
  const handleConfirmSubstitute = async (tomorrow: boolean) => {
    if (!selectedClient) return;

    let message = `Sind Sie sicher, dass Sie die Vertretung für ${selectedClient.first_name} ${selectedClient.last_name} Heute übernehmen möchten?`

    if (tomorrow) {
      message = `Sind Sie sicher, dass Sie die Vertretung für ${selectedClient.first_name} ${selectedClient.last_name} für ${data ? format(data.next_work_day, "EEE d. MMMM", {locale: de}): 'morgen'} übernehmen möchten?`
    }

    // Zusätzliche Bestätigung mit Browser-Alert
    const isConfirmed = window.confirm(message);

    if (!isConfirmed) return;

    setConfirmLoading(true);
    setConfirmError(null);

    try {
      // API-Aufruf zum Übernehmen der Vertretung
      const response = await axiosInstance.post('/client-stand-in', {
        client_id: selectedClient.record_id,
        tomorrow: tomorrow
      });

      setConfirmSuccess(true);
      setShowDetailModal(false);

      // Nach erfolgreicher Bestätigung die Daten neu laden
      refreshData()
        .then(() => {
          // Nach erfolgreichem Refresh zum Dashboard navigieren
          setTimeout(() => {
            navigate('/dashboard');
          }, 500);
        })
        .catch((refreshError) => {
          console.error('Fehler beim Aktualisieren der Daten:', refreshError);
          // Trotz Fehler beim Refresh zum Dashboard navigieren
          setTimeout(() => {
            navigate('/dashboard');
          }, 500);
        });
    } catch (err: any) {
      console.error('Fehler beim Übernehmen der Vertretung:', err);
      const errorMessage = err.response?.data?.message || 'Es ist ein Fehler beim Übernehmen der Vertretung aufgetreten. Bitte versuchen Sie es später erneut.';
      setConfirmError(errorMessage);
    } finally {
      setConfirmLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomContainer>
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Lade Daten...</span>
          </Spinner>
          <p className="mt-3">Daten werden geladen...</p>
        </div>
      </CustomContainer>
    );
  }

  if (error) {
    return (
      <CustomContainer>
        <Alert variant="danger">
          {error}
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3"
            onClick={() => navigate('/dashboard')}
          >
            Zurück zum Dashboard
          </Button>
        </Alert>
      </CustomContainer>
    );
  }

  // Filtere Klienten, die zur Vertretung verfügbar sind
  // (Klienten, die nicht vom aktuellen Mitarbeiter betreut werden und keinen Vertretungsmitarbeiter haben)
  let availableClients = data?.clients || [];
  availableClients = availableClients.filter((c) => c.accompanied_by_employee !== data?.employee_id)

  return (
    <CustomContainer>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Vertretung übernehmen</h1>
          <Alert variant="info" className="mb-0 mt-2">
            Wählen Sie hier einen Klienten zur Vertretung aus. Diese Auswahl muss täglich erneut getroffen werden.
          </Alert>
        </div>
      </div>


      {confirmSuccess && (
        <Alert variant="success">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          Die Vertretung wurde erfolgreich übernommen. Sie werden zum Dashboard weitergeleitet...
        </Alert>
      )}

      {confirmError && (
        <Alert variant="danger" dismissible onClose={() => setConfirmError(null)}>
          {confirmError}
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5 className="mb-0 me-2">Verfügbare Klienten: {availableClients.length}</h5>
          </div>
          <Badge bg="info" className="py-2 px-3">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Nur für heute
          </Badge>
        </Card.Body>
      </Card>

      {availableClients.length === 0 ? (
          <Card className="text-center p-5 shadow-sm">
            <Card.Body>
              <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-info mb-3" />
              <h4>Keine Klienten verfügbar</h4>
              <p className="text-muted">
                Es sind derzeit keine Klienten für eine Vertretung verfügbar.
                Bitte versuchen Sie es später erneut oder wenden Sie sich an Ihren Vorgesetzten.
              </p>
              <Button
                  variant="primary"
                  onClick={() => navigate('/dashboard')}
                  className="mt-3"
              >
                <FontAwesomeIcon icon={faTachometerAlt} className="me-2" />
                Zurück zum Dashboard
              </Button>
            </Card.Body>
          </Card>
      ) : (
          <div className="table-responsive client-table-container shadow-sm">
            <table className="table table-hover align-middle client-table mb-0">
              <thead className="bg-light">
              <tr>
                <th style={{width: '20%'}} className="ps-3">Name</th>
                <th style={{width: '20%'}} className="text-center">Zeit</th>
                <th style={{width: '30%'}}>Schule</th>
                <th style={{width: '10%'}} className="text-center">Vertretung benötigt für</th>
                <th style={{width: '20%'}} className="text-center">Aktion</th>
              </tr>
              </thead>
              <tbody>
              {availableClients.map(client => (
                  <tr
                      key={client.record_id}
                      className="client-table-row"
                      style={{borderLeft: `5px solid ${client.timetable ? '#0dcaf0' : '#6c757d'}`}}
                  >
                    {/* Name des Klienten */}
                    <td className="ps-3" data-label="Name">
                      <div className="fw-bold">{client.first_name} {client.last_name}</div>
                      {client.address && (
                          <div className="small text-muted">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                            {client.address.street ?? ''} {client.address.zip_code ?? ''} {client.address.city ?? ''}
                          </div>
                      )}
                    </td>

                    {/* Zeitinformationen */}
                    <td className="text-center" data-label="Zeit">
                      {client.timetable ? (
                          <div className="time-display bg-info bg-opacity-10 p-2 rounded">
                            <div className="small fw-bold">
                              <FontAwesomeIcon icon={faClock} className="me-1 text-info" />
                              {client.timetable.beginning_school_support || '--:--'}
                            </div>
                            <div className="small text-muted">bis</div>
                            <div className="small fw-bold">{client.timetable.end_school_support || '--:--'}</div>
                          </div>
                      ) : (
                          <span className="text-muted">--:-- - --:--</span>
                      )}
                    </td>

                    {/* Schule */}
                    <td data-label="Schule">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faSchool} className="me-2 text-secondary" />
                        <div className="text-truncate" style={{maxWidth: '200px'}}>
                          {client.school ? (
                              <div>
                                <div className="fw-bold">{client.school.name}</div>
                                <div className="small text-muted">
                                  {client.school.street && <div>{client.school.street}</div>}
                                  {(client.school.zip_code || client.school.city) && (
                                      <div>
                                        {client.school.zip_code} {client.school.city}
                                      </div>
                                  )}
                                </div>
                              </div>
                          ) : (
                              'Keine Schule'
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="text-center" data-label="Vertretung benötigt für">
                      {(() : JSX.Element[] => {
                        const elements = [
                        ];
                        if (
                            client.additional_information?.client_sick_today === false &&
                            client.additional_information.employee_sick_today &&
                            client.additional_information.stand_in_today === 0 &&
                            client.additional_information.today_off === false
                        ) {
                          elements.push(<p key={"today"}>Heute</p>)
                        }
                        if (
                            client.additional_information?.client_sick_next_work_day === false &&
                            client.additional_information.employee_sick_next_work_day &&
                            client.additional_information.stand_in_next_work_day === 0 &&
                            client.additional_information.next_work_day_off === false
                        ) {
                          if (data?.next_work_day) {
                            elements.push(<p key={"next_working_day"}>{format(data.next_work_day, "EEE d. MMMM", {locale: de})}</p>)
                          }
                        }
                        return elements;
                      })()}
                    </td>

                    {/* Action-Button */}
                    <td className="text-center" data-label="Aktion">
                      <Button
                          variant="primary"
                          onClick={() => handleShowDetails(client)}
                          className="d-flex align-items-center justify-content-center mx-auto"
                      >
                        <span className="me-2">Details</span>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Button>
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
      )}

      <div className="mt-5 mb-4 d-flex justify-content-start">
        <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard')}
            className="px-4"
        >
          <FontAwesomeIcon icon={faTachometerAlt} className="me-2" />
          Zurück zum Dashboard
        </Button>
      </div>

      <ClientDetailModal
          show={showDetailModal}
          onHide={handleCloseModal}
          client={selectedClient}
          onConfirm={handleConfirmSubstitute}
          loading={confirmLoading}
          showConfirmButton={true}
          data={data}
      />

    </CustomContainer>
  );
};

export default ClientStandIn;